import AssignmentIcon from '@mui/icons-material/Assignment'
import { blue, yellow } from '@mui/material/colors'
import { ReactComponent as CalendarRecurring } from 'assets/CalendarRecurringIcon.svg'
import { ReactComponent as CalendarOnRequestIcon } from 'assets/CalendarOnRequestIcon.svg'
import { ReactComponent as InvoicedIcon } from 'assets/InvoicedIcon.svg'
import { ReactComponent as ExceptionsIcon } from 'assets/ExceptionsIcon.svg'
import { ReactComponent as DisposalTicketIcon } from 'assets/DisposalTicketIcon.svg'
import { ReactComponent as CurrencyDollarIcon } from 'assets/CurrencyDollarIcon.svg'
import { ReactComponent as GeneralFeeIcon } from 'assets/GeneralFeeIcon.svg'
import { ReactComponent as WorkOrderNote } from 'assets/note/WorkOrderNote.svg'
import { WORK_ORDER_STATUS_ICONS, WORK_ORDER_STATUS_COLORS } from 'settings/constants/workOrder'
import T from 'T'

/**
 * Builds an array of icon configurations based on calendar day data
 */
export const buildIconsArray = ({
  workOrderStatus,
  workOrderCount,
  noteCount,
  showOnRequest,
  requestCount,
  exceptionCount,
  paymentCount,
  feeCount,
  invoiceCount,
  disposalTicketCount,
  showRecurring,
  recurringCount,
  hasRoute,
  routeData,
  onRequestServiceActionIcon,
  onRequestServiceActionName,
  recurringServiceActionIcon,
  recurringServiceActionName,
  iconColor,
  theme,
}) => {
  const icons = []

  if (workOrderStatus && WORK_ORDER_STATUS_ICONS[workOrderStatus]) {
    const StatusIcon = WORK_ORDER_STATUS_ICONS[workOrderStatus]
    icons.push({
      Icon: StatusIcon,
      color: iconColor || theme.palette.grey[600],
      count: workOrderCount,
      label: 'WorkOrderStatus',
      statusColor: WORK_ORDER_STATUS_COLORS[workOrderStatus],
    })
  }

  if (noteCount > 0) {
    icons.push({ Icon: WorkOrderNote, color: iconColor || theme.palette.grey[700], count: noteCount, label: 'Notes' })
  }

  if (showOnRequest) {
    icons.push({ Icon: CalendarOnRequestIcon, color: iconColor || yellow[700], count: requestCount, label: 'OnRequest' })
  }

  if (workOrderCount > 0) {
    icons.push({ Icon: AssignmentIcon, color: iconColor || theme.palette.grey[700], count: workOrderCount, label: 'WorkOrders' })
  }

  if (exceptionCount > 0) {
    icons.push({ Icon: ExceptionsIcon, color: iconColor || theme.palette.error.main, count: exceptionCount, label: 'Exceptions' })
  }

  if (paymentCount > 0) {
    icons.push({ Icon: CurrencyDollarIcon, color: iconColor || theme.palette.grey[600], count: paymentCount, label: 'Payments' })
  }

  if (feeCount > 0) {
    icons.push({ Icon: GeneralFeeIcon, color: iconColor || theme.palette.grey[700], count: feeCount, label: 'Fees' })
  }

  if (invoiceCount > 0) {
    icons.push({ Icon: InvoicedIcon, color: iconColor || '#66BB6A', count: invoiceCount, label: 'Invoices' })
  }

  if (disposalTicketCount > 0) {
    icons.push({
      Icon: DisposalTicketIcon,
      color: iconColor || theme.palette.grey[700],
      count: disposalTicketCount,
      label: 'DisposalTickets',
    })
  }

  if (showRecurring) {
    icons.push({ Icon: CalendarRecurring, color: iconColor || blue[500], count: recurringCount, label: 'Recurring' })
  }

  if (hasRoute) {
    icons.push({
      Icon: null,
      color: iconColor || '#66BB6A',
      count: routeData?.count,
      label: 'Routes',
      routeName: routeData?.routeName,
      routeColor: routeData?.routeColor,
      isUnassigned: false,
    })
  } else if (routeData?.hasUnassignedRoute) {
    // Show unassigned route icon when there are only unassigned routes
    icons.push({
      Icon: null,
      color: iconColor,
      count: routeData?.unassignedCount,
      label: 'Routes',
      routeName: null,
      routeColor: null,
      isUnassigned: true,
    })
  }

  if (onRequestServiceActionIcon) {
    icons.push({
      Icon: onRequestServiceActionIcon,
      color: iconColor || theme.palette.grey[700],
      label: 'OnRequestServiceAction',
      actionName: onRequestServiceActionName,
    })
  }

  if (recurringServiceActionIcon) {
    icons.push({
      Icon: recurringServiceActionIcon,
      color: iconColor || theme.palette.grey[700],
      label: 'RecurringServiceAction',
      actionName: recurringServiceActionName,
    })
  }

  return icons
}

/**
 * Builds the tooltip title string for a calendar day
 */
export const buildTooltipTitle = ({
  dateStr,
  recurringCount,
  requestCount,
  workOrderCount,
  onRequestServiceActionName,
  recurringServiceActionName,
  exceptionCount,
  invoiceCount,
  disposalTicketCount,
  feeCount,
  paymentCount,
  noteCount,
  hasRoute,
  routeData,
}) => {
  const parts = []

  if (requestCount > 0) parts.push(`${T.ON_REQUEST}: ${requestCount}`)
  if (recurringCount > 0) parts.push(`${T.RECURRING}: ${recurringCount}`)
  if (workOrderCount > 0) parts.push(`${T.WORK_ORDERS}: ${workOrderCount}`)
  if (onRequestServiceActionName) parts.push(`${T.ON_REQUEST} ${T.ACTION}: ${onRequestServiceActionName}`)
  if (recurringServiceActionName) parts.push(`${T.RECURRING} ${T.ACTION}: ${recurringServiceActionName}`)
  if (exceptionCount > 0) parts.push(`${T.EXCEPTIONS}: ${exceptionCount}`)
  if (invoiceCount > 0) parts.push(`${T.INVOICES}: ${invoiceCount}`)
  if (disposalTicketCount > 0) parts.push(`${T.DISPOSAL_TICKET}: ${disposalTicketCount}`)
  if (feeCount > 0) parts.push(`${T.FEES}: ${feeCount}`)
  if (paymentCount > 0) parts.push(`${T.PAYMENTS}: ${paymentCount}`)
  if (noteCount > 0) parts.push(`${T.NOTE}: ${noteCount}`)
  if (hasRoute) parts.push(`${T.ROUTE} ${T.ASSIGNED_TO}: ${routeData.count}`)

  return `${dateStr}: ${parts.join(', ')}`
}
